import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Global cache so every SaveButton on the page shares one hydrated set per user.
let cache = { user_id: null, sets: null };
const listeners = new Set();

function notify() {
  listeners.forEach((cb) => cb());
}

async function hydrate(userId) {
  if (cache.user_id === userId && cache.sets) return cache.sets;
  const empty = { jobs: new Set(), listing: new Set(), event: new Set(), need: new Set(), business: new Set() };
  if (!userId) {
    cache = { user_id: null, sets: empty };
    return empty;
  }
  try {
    const { data } = await api.get("/saves");
    cache = {
      user_id: userId,
      sets: {
        jobs: new Set(data.jobs || []),
        listing: new Set(data.listing || []),
        event: new Set(data.event || []),
        need: new Set(data.need || []),
        business: new Set(data.business || []),
      },
    };
  } catch {
    cache = { user_id: userId, sets: empty };
  }
  return cache.sets;
}

/**
 * useSavedItems - reactive hook returning saved IDs per item type + a toggle helper.
 * Type must be one of: 'jobs' | 'listing' | 'event' | 'need' | 'business'.
 */
export function useSavedItems(type) {
  const { user } = useAuth();
  const [ids, setIds] = useState(new Set());
  const [loaded, setLoaded] = useState(false);

  const sync = useCallback(() => {
    const set = cache.sets?.[type] || new Set();
    setIds(new Set(set));
  }, [type]);

  useEffect(() => {
    let alive = true;
    hydrate(user?.id || null).then((sets) => {
      if (!alive) return;
      setIds(new Set(sets[type] || []));
      setLoaded(true);
    });
    listeners.add(sync);
    return () => {
      alive = false;
      listeners.delete(sync);
    };
  }, [user?.id, type, sync]);

  const toggle = async (itemId) => {
    if (!user) return { needsAuth: true };
    const set = cache.sets?.[type];
    if (!set) return { error: true };
    const has = set.has(itemId);
    if (has) set.delete(itemId);
    else set.add(itemId);
    notify();
    try {
      const path = type === "jobs" ? `/saves/jobs/${itemId}` : `/saves/${type}/${itemId}`;
      if (has) await api.delete(path);
      else await api.post(path);
      return { saved: !has };
    } catch (e) {
      if (has) set.add(itemId);
      else set.delete(itemId);
      notify();
      return { error: true };
    }
  };

  const isSaved = (id) => ids.has(id);
  return { savedIds: ids, isSaved, toggle, loaded };
}
